import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;
  /** @type {number} */
  #viewportPrefetchTimeout = 0;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const linkFromCard =
      productCard && typeof productCard.getProductCardLink === 'function' ? productCard.getProductCardLink() : null;
    const linkFromHotspot =
      hotspotProduct && typeof hotspotProduct.getHotspotProductLink === 'function'
        ? hotspotProduct.getHotspotProductLink()
        : null;
    const productLink = linkFromCard || linkFromHotspot;

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard && typeof productCard.getSelectedVariantId === 'function'
      ? productCard.getSelectedVariantId()
      : null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.#setupViewportPrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
    if (this.#viewportPrefetchTimeout) clearTimeout(this.#viewportPrefetchTimeout);
  }

  /**
   * Prefetches product content when the quick-add component enters the viewport (helps on slow/touch when no hover).
   */
  #setupViewportPrefetch() {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url)) return;

    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          if (this.#viewportPrefetchTimeout) {
            clearTimeout(this.#viewportPrefetchTimeout);
            this.#viewportPrefetchTimeout = 0;
          }
          return;
        }
        if (this.#cachedContent.has(this.productPageUrl)) return;

        this.#viewportPrefetchTimeout = window.setTimeout(() => {
          this.#viewportPrefetchTimeout = 0;
          const currentUrl = this.productPageUrl;
          if (currentUrl && !this.#cachedContent.has(currentUrl)) {
            this.fetchProductPage(currentUrl).then((html) => {
              if (html) {
                const gridElement = html.querySelector('[data-product-grid-content]');
                if (gridElement) {
                  this.#cachedContent.set(currentUrl, /** @type {Element} */ (gridElement.cloneNode(true)));
                }
              }
            });
          }
        }, 400);
      },
      { rootMargin: '100px', threshold: 0 }
    );
    this.#viewportObserver.observe(this);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Open modal immediately with loading state so user sees feedback on slow connections
      this.#showModalLoading(true);
      this.#openQuickAddModal();

      const html = await this.fetchProductPage(currentUrl);
      this.#showModalLoading(false);

      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#lazyLoadModalImages();
      this.#openQuickAddModal();
    } else {
      this.#closeQuickAddModal();
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (dialog) {
      dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
    }
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Shows or hides the loading state inside the quick-add modal content area.
   * @param {boolean} show - Whether to show the loading indicator
   */
  #showModalLoading(show) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (show) {
      modalContent.innerHTML =
        '<div class="quick-add-modal__skeleton" aria-live="polite" aria-busy="true">' +
        '<div class="quick-add-modal__skeleton-media"></div>' +
        '<div class="quick-add-modal__skeleton-details">' +
        '<span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-title"></span>' +
        '<span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-price"></span>' +
        '<span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-option"></span>' +
        '<span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-option short"></span>' +
        '<span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-button"></span>' +
        '</div></div>';
    } else {
      modalContent.innerHTML = '';
    }
  }

  /**
   * Sets loading="lazy" on images in the modal so the modal shell appears fast and images load after.
   */
  #lazyLoadModalImages() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const images = modalContent.querySelectorAll('img:not([loading="lazy"])');
    images.forEach((img) => img.setAttribute('loading', 'lazy'));
  }

  /**
   * Fetches the product page content. Tries Section Rendering API first (smaller payload) then full page.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const signal = this.#abortController.signal;

    const trySectionFirst = () => {
      const url = new URL(productPageUrl, window.location.origin);
      const sectionId = 'main';
      url.searchParams.set('section_id', sectionId);
      return url.toString();
    };

    try {
      const sectionUrl = trySectionFirst();
      const response = await fetch(sectionUrl, { signal });

      if (response.ok) {
        const responseText = await response.text();
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        if (doc.querySelector('[data-product-grid-content]')) {
          return doc;
        }
      }

      const fullResponse = await fetch(productPageUrl, { signal });
      if (!fullResponse.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${fullResponse.status}`);
      }
      const responseText = await fullResponse.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      try {
        const fullResponse = await fetch(productPageUrl, { signal });
        if (fullResponse.ok) {
          const responseText = await fullResponse.text();
          return new DOMParser().parseFromString(responseText, 'text/html');
        }
      } catch {
        // ignore fallback error
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#setupExpandVariantsButton(modalContent);
  }

  /**
   * Shows or hides the "Expand all options" button and toggles expanded state on click.
   * @param {Element} modalContent - The quick-add modal content element
   */
  #setupExpandVariantsButton(modalContent) {
    const expandBtn = document.getElementById('quick-add-expand-variants');
    if (!expandBtn || !modalContent) return;

    modalContent.classList.remove('quick-add-modal__content--variants-expanded');

    if (modalContent.querySelector('variant-picker')) {
      expandBtn.hidden = false;
      if (!expandBtn.dataset.quickAddExpandBound) {
        expandBtn.dataset.quickAddExpandBound = 'true';
        expandBtn.addEventListener('click', () => {
          modalContent.classList.toggle('quick-add-modal__content--variants-expanded');
        });
      }
    } else {
      expandBtn.hidden = true;
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const expandBtn = document.getElementById('quick-add-expand-variants');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (expandBtn) expandBtn.hidden = true;
    if (modalContent) modalContent.classList.remove('quick-add-modal__content--variants-expanded');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
